// ProfessionalNotificationSystem.js
// Enterprise-grade notification system with smooth animations
// Provides consistent, accessible notifications across all scenes

import { MobileDetectionService } from './MobileDetectionService.js';
import { professionalAnimations } from './ProfessionalAnimationSystem.js';

/**
 * Professional notification system
 * Provides consistent notifications with enterprise-grade animations
 * Supports multiple notification types and positioning
 */
export class ProfessionalNotificationSystem {
    static _instance = null;
    
    /**
     * Singleton pattern for consistent notification management
     */
    static getInstance() {
        if (!ProfessionalNotificationSystem._instance) {
            ProfessionalNotificationSystem._instance = new ProfessionalNotificationSystem();
        }
        return ProfessionalNotificationSystem._instance;
    }
    
    constructor() {
        if (ProfessionalNotificationSystem._instance) {
            return ProfessionalNotificationSystem._instance;
        }
        
        this.notifications = new Map();
        this.notificationCounter = 0;
        this.maxNotifications = 5;
        this.defaultDuration = 4000;
        
        this._injectNotificationCSS();
        this._createNotificationContainer();
        
        ProfessionalNotificationSystem._instance = this;
    }
    
    /**
     * Show a professional notification
     * @param {string} message - Notification message
     * @param {string} type - Notification type ('success', 'error', 'warning', 'info')
     * @param {Object} options - Configuration options
     * @returns {string} - Notification ID for manual dismissal
     */
    show(message, type = 'info', options = {}) {
        const config = {
            duration: this.defaultDuration,
            persistent: false,
            position: 'top-right',
            showProgress: true,
            allowDismiss: true,
            ...options
        };
        
        const notificationId = `notification-${++this.notificationCounter}`;
        const isMobile = MobileDetectionService.isMobile();
        
        console.log(`📢 ProfessionalNotificationSystem: Showing ${type} notification (${isMobile ? 'mobile' : 'desktop'})`);
        
        // Create notification element
        const notification = this._createNotificationElement(notificationId, message, type, config, isMobile);
        
        // Add to container
        const container = this._getNotificationContainer(config.position);
        container.appendChild(notification);
        
        // Store reference
        this.notifications.set(notificationId, {
            element: notification,
            type,
            config,
            timestamp: Date.now()
        });
        
        // Animate in
        this._animateNotificationIn(notification, config.position);
        
        // Auto-dismiss if not persistent
        if (!config.persistent && config.duration > 0) {
            setTimeout(() => {
                this.dismiss(notificationId);
            }, config.duration);
        }
        
        // Manage notification count
        this._enforceMaxNotifications();
        
        return notificationId;
    }
    
    /**
     * Dismiss a specific notification
     * @param {string} notificationId - Notification ID to dismiss
     */
    dismiss(notificationId) {
        const notification = this.notifications.get(notificationId);
        if (!notification) return;
        
        console.log(`📢 Dismissing notification: ${notificationId}`);
        
        // Animate out
        this._animateNotificationOut(notification.element, () => {
            // Remove from DOM and tracking
            if (notification.element.parentNode) {
                notification.element.parentNode.removeChild(notification.element);
            }
            this.notifications.delete(notificationId);
        });
    }
    
    /**
     * Dismiss all notifications
     */
    dismissAll() {
        console.log('📢 Dismissing all notifications');
        
        const notificationIds = Array.from(this.notifications.keys());
        notificationIds.forEach(id => this.dismiss(id));
    }
    
    /**
     * Show success notification
     * @param {string} message - Success message
     * @param {Object} options - Configuration options
     */
    success(message, options = {}) {
        return this.show(message, 'success', options);
    }
    
    /**
     * Show error notification
     * @param {string} message - Error message
     * @param {Object} options - Configuration options
     */
    error(message, options = {}) {
        return this.show(message, 'error', {
            duration: 6000, // Longer duration for errors
            persistent: false,
            ...options
        });
    }
    
    /**
     * Show warning notification
     * @param {string} message - Warning message
     * @param {Object} options - Configuration options
     */
    warning(message, options = {}) {
        return this.show(message, 'warning', options);
    }
    
    /**
     * Show info notification
     * @param {string} message - Info message
     * @param {Object} options - Configuration options
     */
    info(message, options = {}) {
        return this.show(message, 'info', options);
    }
    
    /**
     * Show loading notification
     * @param {string} message - Loading message
     * @param {Object} options - Configuration options
     */
    loading(message, options = {}) {
        return this.show(message, 'loading', {
            persistent: true,
            showProgress: false,
            allowDismiss: false,
            ...options
        });
    }
    
    /**
     * Create notification element
     * @private
     */
    _createNotificationElement(id, message, type, config, isMobile) {
        const notification = document.createElement('div');
        notification.id = id;
        notification.className = `professional-notification notification-${type}`;
        
        // Mobile-optimized styling
        if (isMobile) {
            notification.classList.add('notification-mobile');
        }
        
        // Icon mapping
        const icons = {
            success: '✅',
            error: '❌', 
            warning: '⚠️',
            info: 'ℹ️',
            loading: '🔄'
        };
        
        const icon = icons[type] || 'ℹ️';
        
        notification.innerHTML = `
            <div class="notification-content">
                <div class="notification-icon">${icon}</div>
                <div class="notification-message">${message}</div>
                ${config.allowDismiss ? '<button class="notification-dismiss" aria-label="Dismiss">×</button>' : ''}
            </div>
            ${config.showProgress ? '<div class="notification-progress"><div class="progress-bar"></div></div>' : ''}
        `;
        
        // Add dismiss handler
        if (config.allowDismiss) {
            const dismissBtn = notification.querySelector('.notification-dismiss');
            dismissBtn.addEventListener('click', () => {
                this.dismiss(id);
            });
        }
        
        // Add progress animation
        if (config.showProgress && config.duration > 0) {
            const progressBar = notification.querySelector('.progress-bar');
            if (progressBar) {
                setTimeout(() => {
                    progressBar.style.width = '0%';
                    progressBar.style.transition = `width ${config.duration}ms linear`;
                }, 100);
            }
        }
        
        return notification;
    }
    
    /**
     * Get or create notification container
     * @private
     */
    _getNotificationContainer(position) {
        const containerId = `notification-container-${position}`;
        let container = document.getElementById(containerId);
        
        if (!container) {
            container = document.createElement('div');
            container.id = containerId;
            container.className = `notification-container notification-${position}`;
            document.body.appendChild(container);
        }
        
        return container;
    }
    
    /**
     * Create notification container
     * @private
     */
    _createNotificationContainer() {
        // Containers are created on-demand in _getNotificationContainer
    }
    
    /**
     * Animate notification in
     * @private
     */
    _animateNotificationIn(notification, position) {
        const isMobile = MobileDetectionService.isMobile();
        
        // Set initial state
        notification.style.opacity = '0';
        notification.style.transform = position.includes('right') ? 'translateX(100%)' : 'translateX(-100%)';
        
        // Animate in
        requestAnimationFrame(() => {
            notification.style.transition = `all ${professionalAnimations.getDuration('normal')}ms cubic-bezier(0.4, 0, 0.2, 1)`;
            notification.style.opacity = '1';
            notification.style.transform = 'translateX(0)';
        });
    }
    
    /**
     * Animate notification out
     * @private
     */
    _animateNotificationOut(notification, callback) {
        const duration = professionalAnimations.getDuration('normal');
        
        notification.style.transition = `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        notification.style.opacity = '0';
        notification.style.transform = 'translateX(100%)';
        notification.style.maxHeight = '0';
        notification.style.marginBottom = '0';
        notification.style.paddingTop = '0';
        notification.style.paddingBottom = '0';
        
        setTimeout(callback, duration);
    }
    
    /**
     * Enforce maximum notification count
     * @private
     */
    _enforceMaxNotifications() {
        if (this.notifications.size <= this.maxNotifications) {
            return;
        }
        
        // Find oldest notification
        let oldestId = null;
        let oldestTimestamp = Date.now();
        
        this.notifications.forEach((notification, id) => {
            if (notification.timestamp < oldestTimestamp) {
                oldestTimestamp = notification.timestamp;
                oldestId = id;
            }
        });
        
        if (oldestId) {
            this.dismiss(oldestId);
        }
    }
    
    /**
     * Inject notification CSS
     * @private
     */
    _injectNotificationCSS() {
        if (document.getElementById('professional-notifications-css')) {
            return; // Already injected
        }
        
        const isMobile = MobileDetectionService.isMobile();
        
        const notificationCSS = `
            <style id="professional-notifications-css">
                /* Professional Notification System - Enterprise Grade */
                
                .notification-container {
                    position: fixed;
                    z-index: 50000;
                    pointer-events: none;
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    max-width: ${isMobile ? '90vw' : '400px'};
                }
                
                .notification-top-right {
                    top: 20px;
                    right: 20px;
                }
                
                .notification-top-left {
                    top: 20px;
                    left: 20px;
                }
                
                .notification-bottom-right {
                    bottom: 20px;
                    right: 20px;
                }
                
                .notification-bottom-left {
                    bottom: 20px;
                    left: 20px;
                }
                
                .professional-notification {
                    background: rgba(30, 41, 59, 0.95);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 16px;
                    padding: 16px 20px;
                    min-height: 60px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
                    pointer-events: auto;
                    position: relative;
                    overflow: hidden;
                    max-width: 100%;
                    word-wrap: break-word;
                }
                
                .notification-mobile {
                    margin: 0 10px;
                    border-radius: 12px;
                    padding: 14px 16px;
                }
                
                .notification-content {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    position: relative;
                    z-index: 1;
                }
                
                .notification-icon {
                    font-size: 20px;
                    line-height: 1;
                    flex-shrink: 0;
                    margin-top: 2px;
                }
                
                .notification-message {
                    flex: 1;
                    font-size: 14px;
                    line-height: 1.4;
                    color: rgba(255, 255, 255, 0.9);
                    font-weight: 500;
                }
                
                .notification-dismiss {
                    background: none;
                    border: none;
                    color: rgba(255, 255, 255, 0.6);
                    font-size: 18px;
                    cursor: pointer;
                    padding: 0;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 200ms ease;
                    flex-shrink: 0;
                }
                
                .notification-dismiss:hover {
                    background: rgba(255, 255, 255, 0.1);
                    color: rgba(255, 255, 255, 0.9);
                }
                
                .notification-progress {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: rgba(255, 255, 255, 0.1);
                    overflow: hidden;
                }
                
                .progress-bar {
                    height: 100%;
                    width: 100%;
                    background: currentColor;
                    transition: width 100ms linear;
                }
                
                /* Type-specific styling */
                .notification-success {
                    border-left: 4px solid #10b981;
                    color: #10b981;
                }
                
                .notification-success .progress-bar {
                    background: #10b981;
                }
                
                .notification-error {
                    border-left: 4px solid #ef4444;
                    color: #ef4444;
                }
                
                .notification-error .progress-bar {
                    background: #ef4444;
                }
                
                .notification-warning {
                    border-left: 4px solid #f59e0b;
                    color: #f59e0b;
                }
                
                .notification-warning .progress-bar {
                    background: #f59e0b;
                }
                
                .notification-info {
                    border-left: 4px solid #00d4ff;
                    color: #00d4ff;
                }
                
                .notification-info .progress-bar {
                    background: #00d4ff;
                }
                
                .notification-loading {
                    border-left: 4px solid #8b5cf6;
                    color: #8b5cf6;
                }
                
                .notification-loading .notification-icon {
                    animation: spin 1s linear infinite;
                }
                
                .notification-loading .progress-bar {
                    background: #8b5cf6;
                    animation: pulse 2s ease-in-out infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @keyframes pulse {
                    0%, 100% { opacity: 0.5; }
                    50% { opacity: 1; }
                }
                
                /* Hover effects */
                .professional-notification:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
                }
                
                /* Mobile optimizations */
                @media (max-width: 768px) {
                    .notification-container {
                        max-width: calc(100vw - 20px);
                        margin: 0 10px;
                    }
                    
                    .notification-top-right,
                    .notification-top-left {
                        top: 10px;
                        left: 10px;
                        right: 10px;
                    }
                    
                    .notification-bottom-right,
                    .notification-bottom-left {
                        bottom: 10px;
                        left: 10px;
                        right: 10px;
                    }
                    
                    .professional-notification {
                        width: 100%;
                        margin: 0;
                    }
                }
                
                /* Accessibility */
                @media (prefers-reduced-motion: reduce) {
                    .professional-notification,
                    .notification-dismiss,
                    .progress-bar {
                        transition: none !important;
                        animation: none !important;
                    }
                }
                
                /* Focus styles for keyboard navigation */
                .notification-dismiss:focus {
                    outline: 2px solid #00d4ff;
                    outline-offset: 2px;
                }
            </style>
        `;
        
        document.head.insertAdjacentHTML('beforeend', notificationCSS);
        console.log('🎨 Professional notification system CSS injected');
    }
    
    /**
     * Create notification container
     * @private
     */
    _createNotificationContainer() {
        // Containers are created on-demand in _getNotificationContainer
    }
    
    /**
     * Get notification container for position
     * @private
     */
    _getNotificationContainer(position) {
        const containerId = `notification-container-${position}`;
        let container = document.getElementById(containerId);
        
        if (!container) {
            container = document.createElement('div');
            container.id = containerId;
            container.className = `notification-container notification-${position}`;
            container.setAttribute('aria-live', 'polite');
            container.setAttribute('aria-label', 'Notifications');
            document.body.appendChild(container);
        }
        
        return container;
    }
    
    /**
     * Animate notification in
     * @private
     */
    _animateNotificationIn(notification, position) {
        const duration = professionalAnimations.getDuration('normal');
        
        // Set initial state
        notification.style.opacity = '0';
        notification.style.transform = position.includes('right') ? 'translateX(100%)' : 'translateX(-100%)';
        notification.style.maxHeight = '0';
        notification.style.marginBottom = '0';
        
        // Animate in
        requestAnimationFrame(() => {
            notification.style.transition = `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
            notification.style.opacity = '1';
            notification.style.transform = 'translateX(0)';
            notification.style.maxHeight = '200px';
            notification.style.marginBottom = '12px';
        });
    }
    
    /**
     * Animate notification out
     * @private
     */
    _animateNotificationOut(notification, callback) {
        const duration = professionalAnimations.getDuration('fast');
        
        notification.style.transition = `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        notification.style.opacity = '0';
        notification.style.transform = 'translateX(100%) scale(0.95)';
        notification.style.maxHeight = '0';
        notification.style.marginBottom = '0';
        notification.style.paddingTop = '0';
        notification.style.paddingBottom = '0';
        
        setTimeout(callback, duration);
    }
    
    /**
     * Get active notifications
     * @returns {Array} - Array of active notification objects
     */
    getActiveNotifications() {
        return Array.from(this.notifications.values());
    }
    
    /**
     * Get notification count
     * @returns {number} - Number of active notifications
     */
    getNotificationCount() {
        return this.notifications.size;
    }
}

// Export singleton instance
export const professionalNotifications = ProfessionalNotificationSystem.getInstance();
export default professionalNotifications;





